import { getItem, setItem } from './application_session_service';
import { createLogger } from '../../lib/logger';
import { OFFLINE_LIMIT_DAYS } from '../../config/env';

const logger = createLogger('OfflineTimeTracker');

/**
 * Service to track when the app was last online
 * Used to enforce offline time limit (default 30 days, configurable via env)
 */

const LAST_ONLINE_KEY = 'last_online_timestamp';

// Minimum time between touchLastOnlineTime writes. Server responses arrive in
// bursts (a sync can fire dozens of requests); one IndexedDB write per minute
// is more than enough resolution for a limit measured in days.
const TOUCH_THROTTLE_MS = 60_000;

let lastTouchMs = 0;

/**
 * Update the last online timestamp to now
 * Call this when the app comes online
 */
export async function updateLastOnlineTime(): Promise<void> {
  const now = new Date().toISOString();
  await setItem(LAST_ONLINE_KEY, now);
  logger.log(`[OfflineTimeTracker] Updated last online time: ${now}`);
}

/**
 * Throttled, never-throwing variant of updateLastOnlineTime for hot paths.
 *
 * Called whenever an HTTP response comes back from the Mon Ami server — any
 * response, even an error status, proves the server was reachable. The
 * browser `online` event only fires on offline→online transitions, so a
 * device that stays online would otherwise never refresh the timestamp and
 * eventually hit the offline limit despite active use.
 *
 * Never rejects: this is a passive freshness marker and must not break the
 * request that triggered it. In practice the underlying setItem already
 * swallows IndexedDB failures (e.g. during the locked-DB window), so the
 * catch here is belt-and-suspenders against that contract changing. A write
 * that fails silently just delays the refresh by one throttle interval —
 * immaterial against a days-scale limit.
 */
export async function touchLastOnlineTime(): Promise<void> {
  const now = Date.now();
  if (now - lastTouchMs < TOUCH_THROTTLE_MS) return;
  lastTouchMs = now;

  try {
    await updateLastOnlineTime();
  } catch (error) {
    logger.warn('[OfflineTimeTracker] Failed to touch last online time:', error);
  }
}

/**
 * Reset the touch throttle (test-only — module state persists across tests)
 */
export function resetTouchThrottle(): void {
  lastTouchMs = 0;
}

/**
 * Get the last online timestamp
 * Returns null if never been online (fresh install)
 */
export async function getLastOnlineTime(): Promise<Date | null> {
  const timestamp = await getItem<string>(LAST_ONLINE_KEY);
  if (!timestamp) {
    return null;
  }
  return new Date(timestamp);
}

/**
 * Check if the app has been offline for more than the configured limit
 * Returns false if never been online (fresh install)
 */
export async function hasExceededOfflineLimit(): Promise<boolean> {
  const lastOnline = await getLastOnlineTime();

  // If never been online, don't block (fresh install or first use)
  if (!lastOnline) {
    return false;
  }

  const now = new Date();
  const diffInMs = now.getTime() - lastOnline.getTime();
  const diffInDays = diffInMs / (1000 * 60 * 60 * 24);

  const exceeded = diffInDays > OFFLINE_LIMIT_DAYS;

  if (exceeded) {
    logger.warn(
      `[OfflineTimeTracker] Offline limit exceeded: ${diffInDays.toFixed(1)} days since last online (limit: ${OFFLINE_LIMIT_DAYS} days)`
    );
  }

  return exceeded;
}

/**
 * Get the number of days since last online (floored for display, e.g. "29 days ago").
 * Returns null if never been online.
 * Note: getDaysRemainingBeforeExpiry intentionally does NOT floor, as it's used for
 * precise threshold comparison. Display formatting is handled by the caller.
 */
export async function getDaysSinceLastOnline(): Promise<number | null> {
  const lastOnline = await getLastOnlineTime();

  if (!lastOnline) {
    return null;
  }

  const now = new Date();
  const diffInMs = now.getTime() - lastOnline.getTime();
  const diffInDays = diffInMs / (1000 * 60 * 60 * 24);

  return Math.floor(diffInDays);
}

/**
 * Get the number of days remaining before offline limit is exceeded
 * Returns null if never been online
 * Returns precise decimal value (e.g., 1.5 days)
 */
export async function getDaysRemainingBeforeExpiry(): Promise<number | null> {
  const lastOnline = await getLastOnlineTime();

  if (!lastOnline) {
    return null;
  }

  const now = new Date();
  const diffInMs = now.getTime() - lastOnline.getTime();
  const diffInDays = diffInMs / (1000 * 60 * 60 * 24);

  const remaining = OFFLINE_LIMIT_DAYS - diffInDays;
  return Math.max(0, remaining);
}

/**
 * Initialize last online time on first use
 * Call this when the app is first authenticated
 */
export async function initializeLastOnlineTime(): Promise<void> {
  const existing = await getLastOnlineTime();

  // Only initialize if not already set
  if (!existing) {
    await updateLastOnlineTime();
    logger.log('[OfflineTimeTracker] Initialized last online time for first use');
  }
}
